/**
 * 未登録品番グループのレビュー画面 — 全モジュールから検出したプレフィックスに
 * 資産カテゴリを割り当てて一括登録する。カテゴリ未選択の行は登録しない。
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { engApi } from './engApi';

export function EngUnknownSkuDialog({ open, onClose, cats, onApplied }) {
  const [rows, setRows] = useState(null);
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);
  const selectable = (cats || []).filter((c) => c.selectable !== false);

  const load = useCallback(() => {
    engApi.unregisteredSkuPrefixes()
      .then((r) => {
        setRows(r || []);
        setPicks(Object.fromEntries((r || [])
          .filter((x) => (cats || []).some((c) => c.id === x.suggested_category_id && c.selectable !== false))
          .map((x) => [x.code, x.suggested_category_id])));
      })
      .catch(() => { setRows([]); toast.error('未登録品番の検出に失敗しました'); });
  }, [cats]);

  useEffect(() => {
    if (!open) { setRows(null); setPicks({}); return; }
    load();
  }, [open, load]);

  const chosen = Object.values(picks).filter(Boolean).length;

  const apply = async () => {
    const mappings = (rows || [])
      .filter((r) => picks[r.code])
      .map((r) => ({ code: r.code, name_ja: r.code, default_category_id: picks[r.code] }));
    if (!mappings.length) { toast.error('カテゴリを選択してください'); return; }
    if (mappings.length > 5
      && !window.confirm(`${mappings.length}件の品番グループを一括登録します。よろしいですか？`)) return;
    setSaving(true);
    try {
      const res = await engApi.bulkMapSkuGroups(mappings);
      toast.success(`品番グループ ${res.created.length}件 を登録し、資産 ${res.assets_classified}件 を分類しました`);
      onApplied?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '登録に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}
        data-testid="eng-unknown-sku-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagnifyingGlass size={18} weight="duotone" className="text-amber-600" />
            未登録品番グループ
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-zinc-500">
          技術資産・設備台帳・貸出・製造・購買・IT資産・発番履歴から検出しました。
          カテゴリを選んだ行だけが登録されます（未設定の行は次回も検出されます）。
        </p>

        {rows === null ? (
          <div className="py-10 text-center text-sm text-zinc-400">検出中…</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-400" data-testid="eng-unknown-sku-empty">
            未登録の品番グループはありません
          </div>
        ) : (
          <div className="divide-y divide-zinc-200 border border-zinc-200 rounded-lg">
            {rows.map((r) => (
              <div key={r.code} className="p-3 flex items-start gap-3" data-testid={`eng-unknown-sku-row-${r.code}`}>
                <div className="w-40 shrink-0">
                  <p className="font-mono font-bold text-teal-700">{r.code}</p>
                  <p className="text-[11px] text-zinc-500 tabular-nums">{r.total_count}件</p>
                  <p className="mt-1 text-[10px] leading-snug text-zinc-400">
                    {(r.sources || []).map((s) => `${s.label} ${s.count}`).join(' / ')}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <select value={picks[r.code] || ''}
                    onChange={(e) => setPicks((p) => ({ ...p, [r.code]: e.target.value }))}
                    className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                    data-testid={`eng-unknown-sku-cat-${r.code}`}>
                    <option value="">（未設定 — 登録しない）</option>
                    {selectable.map((c) => <option key={c.id} value={c.id}>{c.name_ja}</option>)}
                  </select>
                  {r.suggested_category_name && (
                    <p className="mt-1 text-[10px] text-zinc-400">推奨: {r.suggested_category_name}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()}>キャンセル</Button>
          <Button onClick={apply} disabled={saving || !chosen} className="bg-teal-700 hover:bg-teal-800"
            data-testid="eng-unknown-sku-apply">
            {saving ? '登録中…' : `マッピングを作成（${chosen}件）`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
